import { buildSavedViewFiltersJson, parseSavedViewFilters } from '../../shared/saved-views/saved-view-filters';
import { ResultStatus } from '../../models/test-run.model';
import { ReleaseResultsFilters } from '../../models/release-results.model';
import { Release } from '../../models/release.model';
import { gleicheUmgebung } from '../../shared/umgebung-name';
import { RESULT_VIEWS, ResultView } from './test-results.types';

/** Filter der Seite in `SavedView.filtersJson` (page-testresultate.jsx:230). */
export interface TestResultsSavedViewFilters {
  sicht: ResultView | null;
  release: string | null;
  planFilter: string | null;
  env: string;
  resFilter: ResultStatus | 'all';
}

const STORED_FILTER_DEFAULTS: Record<'sicht' | 'release' | 'planFilter' | 'env' | 'resFilter' | 'result', unknown> = {
  sicht: null,
  release: null,
  planFilter: null,
  env: null,
  resFilter: null,
  result: null,
};

const RESULT_FILTER_VALUES: readonly unknown[] = ['all', 'PASSED', 'FAILED', 'BLOCKED', 'SKIPPED'];

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/** Ältere Ansichten tragen nur `env`, `system` und `result`: `result` zählt als `resFilter`, `system` entfällt. */
export function parseTestResultsSavedViewFilters(filtersJson: string | null): TestResultsSavedViewFilters {
  const stored = parseSavedViewFilters(filtersJson, STORED_FILTER_DEFAULTS);
  const resFilter = stored.resFilter ?? stored.result;
  return {
    sicht: RESULT_VIEWS.find((v) => v.id === stored.sicht)?.id ?? null,
    release: textOrNull(stored.release),
    planFilter: textOrNull(stored.planFilter),
    env: textOrNull(stored.env) ?? 'all',
    resFilter: RESULT_FILTER_VALUES.includes(resFilter) ? (resFilter as ResultStatus | 'all') : 'all',
  };
}

export function buildTestResultsSavedViewFiltersJson(filters: TestResultsSavedViewFilters): string {
  return buildSavedViewFiltersJson(filters);
}

/** Seitenfilter für die Release-Endpunkte; Freigabe-Karte und Sicht „Testpläne" zählen damit dieselben Läufe. */
export function releaseResultsFilters(umgebung: string | null): ReleaseResultsFilters {
  return umgebung ? { umgebung } : {};
}

/** Suche der Sichten: Teilstring ohne Groß-/Kleinschreibung; eine leere Suche passt immer. */
export function passtZurSuche(suche: string, felder: readonly (string | null)[]): boolean {
  const begriff = suche.trim().toLowerCase();
  return !begriff || felder.some((feld) => feld?.toLowerCase().includes(begriff));
}

export function releasesAbsteigend(releases: readonly Release[]): Release[] {
  return [...releases].sort((a, b) => b.id.localeCompare(a.id, undefined, { numeric: true }));
}

export interface WaehlbareTeststufe {
  name: string;
  umgebung: string;
}

/**
 * Der Name ist die Kennung in der URL (`stufe=`), aber nicht eindeutig: Die Stufen kommen nach Position,
 * die erste eines Namens gewinnt. Eine Stufe ohne Umgebung setzt keinen Filter und steht nicht zur Wahl.
 */
export function waehlbareTeststufen(release: Release | null): WaehlbareTeststufe[] {
  const stufen = new Map<string, WaehlbareTeststufe>();
  for (const { stageName, environment } of release?.testStages ?? []) {
    if (stageName && environment && !stufen.has(stageName)) {
      stufen.set(stageName, { name: stageName, umgebung: environment });
    }
  }
  return [...stufen.values()];
}

export function stufeZurUmgebung(stufen: WaehlbareTeststufe[], umgebung: string): WaehlbareTeststufe | null {
  return stufen.find((s) => gleicheUmgebung(s.umgebung, umgebung)) ?? null;
}

export type PassRateTone = 'good' | 'warn' | 'bad';

export function passRateTone(rate: number): PassRateTone {
  if (rate >= 90) return 'good';
  if (rate >= 75) return 'warn';
  return 'bad';
}
